import React, { useEffect, useState } from 'react';
import { Field, FieldArray, Form, Formik } from 'formik';
import { Box, Button, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import { Add, Delete, Edit, Visibility } from '@mui/icons-material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import {
  AutoCompleteSelectUI,
  CheckboxUI,
  DeleteDialogUI,
  DialogUI,
  FormBase,
  FormButtonPanelUI,
  FormErrorUI,
  FormHeaderUI,
  FormLabelUI,
  InputUI,
} from '../Interface';
import HomeScreenReports from './HomeScreenReports';
import HomeScreenTileDialog from './HomeScreenTileDialog';
import { HomeScreenPreview } from './HomeScreen';
import homeScreenService from '../../service/homeScreen.service';
import biService from '../../service/bi.service';
import masterDataService from '../../service/masterData.service';
import userService from '../../service/user.service';
import useMenus from '../../context/MenuContext';
import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { getIcon } from '../../utils/helper';
import { NotificationType } from '../../config';
import homeScreenBackground from '../../assets/homeScreen/home-screen-background.jpg';
import {
  getHomeScreenBackgroundImageSource,
  getHomeScreenImageSource,
  HomeScreenTileTypes,
  normalizeHomeScreenBackgroundImage,
  normalizeHomeScreenImage,
} from '../../config/homeScreen';

let clientId = 0;
const nextClientId = (prefix) => `${prefix}-${clientId++}`;

const defaultValues = {
  name: '',
  description: '',
  backgroundImageDataBase64: null,
  backgroundImageMimeType: null,
  _backgroundImageChanged: false,
  active: true,
  isUserSelectable: false,
  settings: null,
  tiles: [],
  tileGroups: [],
  userGroups: [],
  userLinks: [],
};

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required(),
  description: Yup.string().nullable(),
  active: Yup.boolean(),
  isUserSelectable: Yup.boolean(),
  tiles: Yup.array().required(),
  tileGroups: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().trim().required(),
      tiles: Yup.array().required(),
    })
  ),
  userGroups: Yup.array().of(
    Yup.object().shape({
      userGroupId: Yup.number().required(),
    })
  ),
});

const withClientIds = (homeScreen) => ({
  ...defaultValues,
  ...normalizeHomeScreenBackgroundImage(homeScreen),
  tiles: (homeScreen?.tiles ?? []).map((tile) => ({
    ...normalizeHomeScreenImage(tile),
    _clientId: nextClientId('tile'),
  })),
  tileGroups: (homeScreen?.tileGroups ?? []).map((group) => ({
    ...group,
    _clientId: nextClientId('group'),
    tiles: (group.tiles ?? []).map((tile) => ({
      ...normalizeHomeScreenImage(tile),
      _clientId: nextClientId('tile'),
    })),
  })),
  userGroups: (homeScreen?.userGroupLinks ?? homeScreen?.userGroups ?? []).map((item) => ({
    userGroupId: item.userGroupId,
  })),
  userLinks: homeScreen?.userLinks ?? [],
});

const normalizeTiles = (tiles) => tiles.map((tile, visOrder) => ({ ...tile, visOrder }));
const normalizeGroups = (groups) => groups.map((group, visOrder) => ({ ...group, visOrder }));

const cleanTile = (tile, visOrder) => {
  const data = {
    type: tile.type,
    title: tile.title || null,
    icon: tile.type === HomeScreenTileTypes.STATIC_TEXT ? tile.icon || null : null,
    width: Number(tile.width),
    height: Number(tile.height),
    visOrder,
    openInNewTab: Boolean(tile.openInNewTab),
    settings: tile.settings ?? null,
  };

  if (tile.type === HomeScreenTileTypes.BI_REPORT) data.biReportId = Number(tile.biReportId);
  if (tile.type === HomeScreenTileTypes.IMAGE) {
    data.imageDataBase64 = getHomeScreenImageSource(tile);
    data.imageMimeType = tile.imageMimeType;
    data.imageAltText = tile.imageAltText || null;
  }
  if (tile.type === HomeScreenTileTypes.STATIC_TEXT) data.textContent = tile.textContent;
  if (tile.linkType && tile.linkTarget) {
    data.linkType = tile.linkType;
    data.linkTarget = tile.linkTarget;
  }

  return data;
};

const cleanPayload = (values) => ({
  name: values.name.trim(),
  description: values.description || null,
  ...(values._backgroundImageChanged && {
    backgroundImageDataBase64: values.backgroundImageDataBase64,
    backgroundImageMimeType: values.backgroundImageMimeType,
  }),
  active: Boolean(values.active),
  isUserSelectable: Boolean(values.isUserSelectable),
  settings: values.settings ?? null,
  tiles: normalizeTiles(values.tiles).map(cleanTile),
  tileGroups: normalizeGroups(values.tileGroups).map((group, visOrder) => ({
    name: group.name.trim(),
    visOrder,
    tiles: normalizeTiles(group.tiles).map(cleanTile),
  })),
  userGroups: values.userGroups.map((item) => ({
    userGroupId: Number(item.userGroupId),
  })),
});

const readBackgroundImage = (file, setFieldValue) => {
  if (!file) return;

  if (file.size > 10 * 1024 * 1024) {
    addNotification({ content: 'A háttérkép legfeljebb 10 MB lehet.', type: NotificationType.ERROR });
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    setFieldValue('backgroundImageDataBase64', String(reader.result));
    setFieldValue('backgroundImageMimeType', file.type);
    setFieldValue('_backgroundImageChanged', true);
  };
  reader.readAsDataURL(file);
};

const getTileList = (values, droppableId) => {
  if (droppableId === 'tiles-outside') return values.tiles;
  const group = values.tileGroups.find((item) => `group-${item._clientId}` === droppableId);
  return group?.tiles ?? [];
};

const setTileList = (values, setFieldValue, droppableId, tiles) => {
  if (droppableId === 'tiles-outside') return setFieldValue('tiles', normalizeTiles(tiles));
  const groupIndex = values.tileGroups.findIndex((item) => `group-${item._clientId}` === droppableId);
  return setFieldValue(`tileGroups.${groupIndex}.tiles`, normalizeTiles(tiles));
};

const TileRow = ({ tile, index, disabled, onEdit, onDelete }) => (
  <Draggable draggableId={tile._clientId} index={index} isDragDisabled={disabled}>
    {(provided, snapshot) => (
      <Paper
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        elevation={0}
        style={provided.draggableProps.style}
        sx={{
          gridColumn: {
            xs: `span ${Math.min(Math.max(1, Number(tile.width) || 1), 2)}`,
            sm: `span ${Math.min(Math.max(1, Number(tile.width) || 1), 4)}`,
            lg: `span ${Math.min(Math.max(1, Number(tile.width) || 1), 6)}`,
          },
          gridRow: `span ${Math.max(1, Number(tile.height) || 1)}`,
          position: 'relative',
          display: 'flex',
          minHeight: 0,
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid',
          borderColor: snapshot.isDragging ? '#0a6ed1' : 'rgba(255,255,255,0.8)',
          bgcolor: 'rgba(255,255,255,0.95)',
          p: 1.5,
          boxShadow: snapshot.isDragging
            ? '0 10px 28px rgba(10,110,209,0.22)'
            : '0 2px 8px rgba(15,23,42,0.12), 0 10px 24px rgba(15,23,42,0.08)',
          transition: 'box-shadow 180ms ease, border-color 180ms ease',
          '&:hover': {
            borderColor: 'rgba(10,110,209,0.4)',
            boxShadow: '0 6px 18px rgba(15,23,42,0.16)',
          },
          '&:hover [data-tile-actions], &:focus-within [data-tile-actions]': { opacity: 1 },
        }}
      >
        <Box sx={{ minWidth: 0, pr: 8 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography noWrap sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: '#1e293b' }}>
              {tile.title || 'Névtelen csempe'}
            </Typography>
          </Stack>
          <Typography sx={{ mt: 0.25, fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b' }}>
            {tile.type} · {tile.width} × {tile.height}
          </Typography>
        </Box>
        <Box sx={{ mt: 1, minHeight: 0, flex: 1, overflow: 'hidden', borderRadius: 2, border: '1px solid #f1f5f9', bgcolor: '#f7f9fb', p: 1, fontSize: 14, color: '#475569' }}>
          {tile.type === HomeScreenTileTypes.IMAGE && tile.imageDataBase64 && (
            <Box
              component="img"
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              src={getHomeScreenImageSource(tile)}
              alt={tile.imageAltText ?? ''}
            />
          )}
          {tile.type === HomeScreenTileTypes.STATIC_TEXT && (
            <Stack direction="row" alignItems="stretch" spacing={0.75} sx={{ height: '100%' }}>
              <Typography component="div" sx={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', fontSize: 'inherit' }}>{tile.textContent}</Typography>
              {tile.icon && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  {getIcon(tile.icon, '2rem', '#6baed6')}
                </Box>
              )}
            </Stack>
          )}
          {tile.type === HomeScreenTileTypes.BI_REPORT && <Typography sx={{ fontWeight: 500 }}>BI riport: {tile.biReportId}</Typography>}
        </Box>
        {!disabled && (
          <Box data-tile-actions="" sx={{ position: 'absolute', right: 4, top: 4, flexShrink: 0, borderRadius: 2, border: '1px solid rgba(226,232,240,0.6)', bgcolor: 'rgba(255,255,255,0.95)', opacity: 0, boxShadow: 1, transition: 'opacity 150ms ease' }}>
            <IconButton size="small" aria-label="Csempe szerkesztése" onClick={onEdit}>
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" aria-label="Csempe törlése" onClick={onDelete}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>
    )}
  </Draggable>
);

const TileContainer = ({ id, title, tiles, disabled, onAdd, onEdit, onDelete }) => (
  <Box sx={{ py: 1 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography component="h3" sx={{ fontWeight: 600, color: '#334155' }}>{title}</Typography>
      {!disabled && (
        <Button size="small" startIcon={<Add />} onClick={onAdd}>
          Csempe
        </Button>
      )}
    </Stack>
    <Droppable droppableId={id} type="TILE">
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            display: 'grid',
            minHeight: 120,
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
              lg: 'repeat(6, minmax(0, 1fr))',
            },
            gridAutoRows: '110px',
            gap: 1,
            borderRadius: 1,
            p: 0.5,
            bgcolor: snapshot.isDraggingOver ? '#eff6ff' : 'transparent',
          }}
        >
          {tiles.map((tile, index) => (
            <TileRow
              key={tile._clientId}
              tile={tile}
              index={index}
              disabled={disabled}
              onEdit={() => onEdit(index)}
              onDelete={() => onDelete(index)}
            />
          ))}
          {provided.placeholder}
        </Box>
      )}
    </Droppable>
  </Box>
);

const HomeScreenAdmin = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [selectedValues, setSelectedValues] = useState({});
  const [initialValues, setInitialValues] = useState(defaultValues);
  const [viewField, setViewField] = useState(true);
  const [refetchTable, setRefetchTable] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLayoutDialog, setShowLayoutDialog] = useState(false);
  const [layoutPreviewMode, setLayoutPreviewMode] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [biReports, setBiReports] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignmentUserId, setAssignmentUserId] = useState(null);
  const [tileDialog, setTileDialog] = useState(null);

  useEffect(() => {
    getMenus();
    Promise.all([
      biService.allBiReports(),
      masterDataService.userGroupsToForm(),
      userService.getUsers({
        filter: { filterModel: { items: [], logicOperator: 'and' } },
        sorting: { sortModel: [{ field: 'nickName', sort: 'asc' }] },
        pagination: { paginationModel: { page: 0, pageSize: 100 } },
      }),
    ]).then(([reports, groups, userRows]) => {
      setBiReports((reports ?? []).map((item) => ({ value: item.id, label: item.name })));
      setUserGroups(groups ?? []);
      setUsers(
        (userRows?.rows ?? []).map((item) => ({
          value: item.userId,
          label: `${item.nickName} (${item.userName})`,
        }))
      );
    });
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('homescreenAdmin');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (!selectedValues?.id) return;
    homeScreenService.getById(selectedValues.id).then((data) => setInitialValues(withClientIds(data)));
  }, [selectedValues?.id]);

  const closeEditor = () => {
    setViewField(true);
    setShowLayoutDialog(false);
    setLayoutPreviewMode(false);
    setTileDialog(null);
  };

  const submit = (values) => {
    const payload = cleanPayload(values);
    const saveRequest = values.id
      ? homeScreenService.update(values.id, payload)
      : homeScreenService.create(payload);

    return saveRequest.then((data) => {
      addNotification({ content: 'A kezdőképernyő mentése sikeres.', type: NotificationType.SUCCESS });
      setInitialValues(withClientIds(data));
      setSelectedValues(data);
      setViewField(true);
      setShowLayoutDialog(false);
      setLayoutPreviewMode(false);
      setRefetchTable(true);
    });
  };

  const remove = () => {
    homeScreenService.remove(selectedValues.id).then(() => {
      addNotification({ content: 'Sikeres törlés!', type: NotificationType.WARNING });
      setSelectedValues({});
      setInitialValues(defaultValues);
      setShowDeleteDialog(false);
      setRefetchTable(true);
    });
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        enableReinitialize
        onSubmit={submit}
      >
        {({ values, errors, isSubmitting, setFieldValue, setValues, resetForm }) => {
          const editTile = (containerId, index) => {
            const tiles = getTileList(values, containerId);
            setTileDialog({ containerId, index, tile: tiles[index] });
          };

          const deleteTile = (containerId, index) => {
            const tiles = [...getTileList(values, containerId)];
            tiles.splice(index, 1);
            setTileList(values, setFieldValue, containerId, tiles);
          };

          const saveTile = (tile) => {
            const tiles = [...getTileList(values, tileDialog.containerId)];
            const nextTile = {
              ...tileDialog.tile,
              ...tile,
              _clientId: tileDialog.tile?._clientId ?? nextClientId('tile'),
            };
            if (tileDialog.index === null) tiles.push(nextTile);
            else tiles[tileDialog.index] = nextTile;
            setTileList(values, setFieldValue, tileDialog.containerId, tiles);
            setTileDialog(null);
          };

          const onDragEnd = (result) => {
            if (!result.destination) return;

            if (result.type === 'GROUP') {
              const groups = [...values.tileGroups];
              const [moved] = groups.splice(result.source.index, 1);
              groups.splice(result.destination.index, 0, moved);
              setFieldValue('tileGroups', normalizeGroups(groups));
              return;
            }

            const sourceTiles = [...getTileList(values, result.source.droppableId)];
            const [moved] = sourceTiles.splice(result.source.index, 1);
            if (result.source.droppableId === result.destination.droppableId) {
              sourceTiles.splice(result.destination.index, 0, moved);
              setTileList(values, setFieldValue, result.source.droppableId, sourceTiles);
              return;
            }
            const destinationTiles = [...getTileList(values, result.destination.droppableId)];
            destinationTiles.splice(result.destination.index, 0, moved);
            setTileList(values, setFieldValue, result.source.droppableId, sourceTiles);
            setTileList(values, setFieldValue, result.destination.droppableId, destinationTiles);
          };

          const allItemsPersisted =
            Boolean(values.id) &&
            values.tiles.every((tile) => tile.id) &&
            values.tileGroups.every((group) => group.id && group.tiles.every((tile) => tile.id));

          const saveOrder = () => {
            const order = {
              tileGroups: values.tileGroups.map((group, visOrder) => ({ id: group.id, visOrder })),
              tiles: [
                ...values.tiles.map((tile, visOrder) => ({ id: tile.id, visOrder, tileGroupId: null })),
                ...values.tileGroups.flatMap((group) =>
                  group.tiles.map((tile, visOrder) => ({ id: tile.id, visOrder, tileGroupId: group.id }))
                ),
              ],
            };
            homeScreenService.updateOrder(values.id, order).then((data) => {
              const refreshedValues = withClientIds(data);
              addNotification({ content: 'A sorrend mentése sikeres.', type: NotificationType.SUCCESS });
              setValues(refreshedValues);
              setInitialValues(refreshedValues);
              setRefetchTable(true);
            });
          };

          const removeDirectUserAssignment = (userId) => {
            homeScreenService.removeUserAssignment(userId).then(() => {
              setFieldValue(
                'userLinks',
                (values.userLinks ?? []).filter((item) => Number(item.userId) !== Number(userId))
              );
              if (Number(assignmentUserId) === Number(userId)) setAssignmentUserId(null);
              addNotification({
                content: 'A közvetlen user-hozzárendelés törlése sikeres.',
                type: NotificationType.WARNING,
              });
            });
          };

          return (
            <FormBase
              name="HomeScreen"
              leftPanelContent={
                <Box sx={{ height: '100%' }}>
                  <FormHeaderUI
                    addClick={() => {
                      setSelectedValues({});
                      setInitialValues(withClientIds(defaultValues));
                      setViewField(false);
                    }}
                    addDisabled={!viewField || !canEdit}
                    modClick={() => setViewField(false)}
                    modDisabled={!viewField || !selectedValues?.id || !canEdit}
                    deleteClick={() => setShowDeleteDialog(true)}
                    deleteDisabled={!viewField || !selectedValues?.id || !canEdit}
                  />
                  <Form>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                      <Box sx={{ gridColumn: { md: 'span 2' } }}>
                        <Field
                          name="name"
                          as={InputUI}
                          fullWidth
                          disabled={viewField}
                          variant="standard"
                          label={<FormLabelUI text="Név" />}
                          helperText={<FormErrorUI message={errors.name} />}
                        />
                      </Box>
                      <Box sx={{ gridColumn: { md: 'span 2' } }}>
                        <Field
                          name="description"
                          as={InputUI}
                          fullWidth
                          multiline
                          disabled={viewField}
                          variant="standard"
                          label={<FormLabelUI text="Leírás" />}
                        />
                      </Box>
                      <CheckboxUI
                        checked={Boolean(values.active)}
                        disabled={viewField}
                        onCheckedChange={(checked) => setFieldValue('active', checked)}
                        label={<FormLabelUI text="Aktív" />}
                      />
                      <CheckboxUI
                        checked={Boolean(values.isUserSelectable)}
                        disabled={viewField}
                        onCheckedChange={(checked) => setFieldValue('isUserSelectable', checked)}
                        label={<FormLabelUI text="User által választható" />}
                      />
                      <Box sx={{ gridColumn: { md: 'span 2' } }}>
                        <FormLabelUI text="Kezdőképernyő háttere" />
                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                          <Button component="label" type="button" variant="outlined" disabled={viewField}>
                            Háttérkép kiválasztása
                            <Box
                              component="input"
                              sx={{ display: 'none' }}
                              type="file"
                              accept="image/*"
                              onChange={(event) => {
                                readBackgroundImage(event.target.files?.[0], setFieldValue);
                                event.target.value = '';
                              }}
                            />
                          </Button>
                          <Button
                            type="button"
                            color="inherit"
                            disabled={viewField || !values.backgroundImageDataBase64}
                            onClick={() => {
                              setFieldValue('backgroundImageDataBase64', null);
                              setFieldValue('backgroundImageMimeType', null);
                              setFieldValue('_backgroundImageChanged', true);
                            }}
                          >
                            Alapértelmezett háttér visszaállítása
                          </Button>
                        </Stack>
                        <Typography sx={{ mt: 0.75, fontSize: 12, color: '#64748b' }}>
                          Legfeljebb 10 MB. Mentéskor a kép automatikusan optimalizálásra kerül.
                        </Typography>
                        <Box
                          component="img"
                          src={getHomeScreenBackgroundImageSource(values) || homeScreenBackground}
                          alt="Kezdőképernyő hátterének előnézete"
                          sx={{
                            mt: 1.5,
                            width: '100%',
                            maxHeight: 220,
                            borderRadius: 2,
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2.5 }}>
                      <Button
                        type="button"
                        variant="contained"
                        disabled={viewField}
                        onClick={() => {
                          setLayoutPreviewMode(false);
                          setShowLayoutDialog(true);
                        }}
                      >
                        Csempék és csoportok vizuális szerkesztése
                      </Button>
                    </Box>

                    <DialogUI
                      open={showLayoutDialog}
                      onClose={() => {
                        setShowLayoutDialog(false);
                        setLayoutPreviewMode(false);
                      }}
                      headerContent="Kezdőképernyő rács szerkesztése"
                      fullScreen
                      closeBtn
                      contentStyle={{ backgroundColor: '#f3f4f6', padding: '12px' }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mb: 1.5 }}>
                        {!layoutPreviewMode && values.id && (
                          <Button variant="outlined" disabled={!allItemsPersisted} onClick={saveOrder}>
                            Csak a sorrend mentése
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          startIcon={layoutPreviewMode ? <Edit /> : <Visibility />}
                          onClick={() => setLayoutPreviewMode((previous) => !previous)}
                        >
                          {layoutPreviewMode ? 'Szerkesztés' : 'Előnézet'}
                        </Button>
                      </Stack>
                      {layoutPreviewMode ? (
                        <HomeScreenPreview homeScreen={values} showBackground />
                      ) : (
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Stack spacing={2}>
                        <TileContainer
                          id="tiles-outside"
                          title="Csoporton kívüli csempék"
                          tiles={values.tiles}
                          disabled={viewField}
                          onAdd={() => setTileDialog({ containerId: 'tiles-outside', index: null, tile: null })}
                          onEdit={(index) => editTile('tiles-outside', index)}
                          onDelete={(index) => deleteTile('tiles-outside', index)}
                        />

                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 1 }}>
                          <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600 }}>Vizuális csempecsoportok</Typography>
                          {!viewField && (
                            <Button
                              startIcon={<Add />}
                              onClick={() =>
                                setFieldValue('tileGroups', [
                                  ...values.tileGroups,
                                  {
                                    name: 'Új csempecsoport',
                                    visOrder: values.tileGroups.length,
                                    tiles: [],
                                    _clientId: nextClientId('group'),
                                  },
                                ])
                              }
                            >
                              Csoport
                            </Button>
                          )}
                        </Stack>
                        <Droppable droppableId="tile-groups" type="GROUP">
                          {(provided) => (
                            <Stack ref={provided.innerRef} {...provided.droppableProps} spacing={1.5}>
                              {values.tileGroups.map((group, groupIndex) => {
                                const containerId = `group-${group._clientId}`;
                                return (
                                  <Draggable
                                    key={group._clientId}
                                    draggableId={group._clientId}
                                    index={groupIndex}
                                    isDragDisabled={viewField}
                                  >
                                    {(groupProvided) => (
                                      <Box
                                        ref={groupProvided.innerRef}
                                        {...groupProvided.draggableProps}
                                        sx={{ borderBottom: '1px solid #cbd5e1', pb: 2 }}
                                      >
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} {...groupProvided.dragHandleProps}>
                                          <Field
                                            name={`tileGroups.${groupIndex}.name`}
                                            as={InputUI}
                                            fullWidth
                                            disabled={viewField}
                                            variant="standard"
                                            label={<FormLabelUI text="Csoport neve" />}
                                            helperText={<FormErrorUI message={errors.tileGroups?.[groupIndex]?.name} />}
                                          />
                                          {!viewField && (
                                            <IconButton
                                              color="error"
                                              aria-label="Csoport törlése"
                                              onClick={() => {
                                                const groups = [...values.tileGroups];
                                                const [removed] = groups.splice(groupIndex, 1);
                                                setFieldValue('tiles', normalizeTiles([...values.tiles, ...removed.tiles]));
                                                setFieldValue('tileGroups', normalizeGroups(groups));
                                              }}
                                            >
                                              <Delete />
                                            </IconButton>
                                          )}
                                        </Stack>
                                        <TileContainer
                                          id={containerId}
                                          title="Csempék"
                                          tiles={group.tiles}
                                          disabled={viewField}
                                          onAdd={() => setTileDialog({ containerId, index: null, tile: null })}
                                          onEdit={(index) => editTile(containerId, index)}
                                          onDelete={(index) => deleteTile(containerId, index)}
                                        />
                                      </Box>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </Stack>
                          )}
                        </Droppable>
                        </Stack>
                      </DragDropContext>
                      )}
                    </DialogUI>

                    <FieldArray name="userGroups">
                      {({ push, remove }) => (
                        <Paper variant="outlined" sx={{ mt: 2.5, borderRadius: 2, p: 1.5 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                            <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600 }}>Jogosultsági csoportok</Typography>
                            {!viewField && (
                              <Button startIcon={<Add />} onClick={() => push({ userGroupId: null })}>
                                Hozzárendelés
                              </Button>
                            )}
                          </Stack>
                          {values.userGroups.map((assignment, index) => (
                            <Box key={index} sx={{ mb: 1, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 44px', alignItems: 'start', gap: 1 }}>
                              <Field
                                name={`userGroups.${index}.userGroupId`}
                                component={AutoCompleteSelectUI}
                                fullWidth
                                disabled={viewField}
                                variant="standard"
                                dataset={userGroups}
                                selectedValue={assignment.userGroupId}
                                selectedLabelValue={
                                  userGroups.find((item) => item.value === assignment.userGroupId)?.label ?? ''
                                }
                                label={<FormLabelUI text="Jogosultsági csoport" />}
                                onChange={(_event, value) =>
                                  setFieldValue(`userGroups.${index}.userGroupId`, value?.value ?? null)
                                }
                                helperText={<FormErrorUI message={errors.userGroups?.[index]?.userGroupId} />}
                              />
                              {!viewField && (
                                <IconButton color="error" aria-label="Hozzárendelés törlése" onClick={() => remove(index)}>
                                  <Delete />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                        </Paper>
                      )}
                    </FieldArray>

                    {values.id && (
                      <Paper variant="outlined" sx={{ mt: 2.5, borderRadius: 2, p: 1.5 }}>
                        <Typography component="h2" sx={{ mb: 1.5, fontSize: 18, fontWeight: 600 }}>Közvetlen user-hozzárendelés</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto auto' }, alignItems: 'center', gap: 1 }}>
                          <AutoCompleteSelectUI
                            fullWidth
                            variant="standard"
                            dataset={users}
                            selectedValue={assignmentUserId}
                            selectedLabelValue={
                              users.find((item) => item.value === assignmentUserId)?.label ?? ''
                            }
                            label={<FormLabelUI text="User" />}
                            onChange={(_event, value) => setAssignmentUserId(value?.value ?? null)}
                          />
                          <Button
                            variant="contained"
                            disabled={!assignmentUserId || !canEdit}
                            onClick={() =>
                              homeScreenService.setUserAssignment(assignmentUserId, values.id).then((userLink) => {
                                setFieldValue('userLinks', [
                                  ...(values.userLinks ?? []).filter(
                                    (item) => Number(item.userId) !== Number(userLink.userId)
                                  ),
                                  userLink,
                                ]);
                                setAssignmentUserId(null);
                                addNotification({
                                  content: 'A közvetlen user-hozzárendelés mentése sikeres.',
                                  type: NotificationType.SUCCESS,
                                });
                              })
                            }
                          >
                            Hozzárendelés
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            disabled={!assignmentUserId || !canEdit}
                            onClick={() => removeDirectUserAssignment(assignmentUserId)}
                          >
                            Törlés
                          </Button>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 600, color: '#475569' }}>
                            Már közvetlenül hozzárendelt felhasználók
                          </Typography>
                          {(values.userLinks?.length ?? 0) > 0 ? (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {values.userLinks.map((userLink) => (
                                <Chip
                                  key={userLink.userId}
                                  label={
                                    users.find((item) => Number(item.value) === Number(userLink.userId))?.label ??
                                    `User #${userLink.userId}`
                                  }
                                  variant="outlined"
                                  onDelete={canEdit ? () => removeDirectUserAssignment(userLink.userId) : undefined}
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Typography sx={{ fontSize: 14, color: '#64748b' }}>
                              Nincs közvetlenül hozzárendelt felhasználó.
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    )}

                    {!viewField && (
                      <FormButtonPanelUI
                        disabled={isSubmitting}
                        onCancelClick={() => {
                          closeEditor();
                          resetForm();
                        }}
                      />
                    )}
                  </Form>
                  <HomeScreenTileDialog
                    open={Boolean(tileDialog)}
                    onClose={() => setTileDialog(null)}
                    onSave={saveTile}
                    tile={tileDialog?.tile}
                    biReports={biReports}
                  />
                </Box>
              }
              rightPanelContent={
                <HomeScreenReports
                  refetch={{ refetchTable, setRefetchTable }}
                  selection={{ selectedValues, setSelectedValues }}
                  viewField={viewField}
                />
              }
            />
          );
        }}
      </Formik>
      <DeleteDialogUI
        handleRemoveElement={remove}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default HomeScreenAdmin;
